/**
 * Turn a clicked element into a tour anchor, and grade how durable that anchor is.
 *
 * The grade is the point. An author cannot be expected to know which attributes
 * survive a refactor, so the picker says it plainly and — for the fragile case —
 * hands them a copy-ready JSX snippet to give an engineer. That is what makes
 * the `data-fg-tour` convention spread as tours get authored, instead of
 * requiring a one-time audit of every element in the app before anyone can
 * write a step.
 */

import { TOUR_ANCHOR_GRADE, type TourAnchor, type TourAnchorGrade } from '../tourTypes'
import { countAnchorMatches } from '../anchor'

export type AnchorCandidate = {
  anchor: TourAnchor
  grade: TourAnchorGrade
  /** What the author is told about durability. */
  advice: string
  /** For grade D: the attribute an engineer should add. */
  suggestedAttribute?: string
  /** How many visible elements this anchor currently matches. */
  matches: number
}

const GRADE_ADVICE: Record<TourAnchorGrade, string> = {
  A: 'Stable — this attribute exists for the tour.',
  B: 'Good, but test-owned. It may be renamed without warning.',
  D: 'Fragile. This path breaks on the next refactor.',
}

const label = (el: Element, fallback: string): string => {
  const text = (el.getAttribute('aria-label') || el.textContent || '').trim().replace(/\s+/g, ' ')
  return text ? text.slice(0, 60) : fallback
}

/**
 * A CSS path good enough to find the element again today, used only as the
 * escape hatch. Kept short — a long descendant chain is more brittle, not less.
 */
export function derivedCssPath(el: Element): string {
  const parts: string[] = []
  let node: Element | null = el
  for (let depth = 0; node && depth < 4 && node !== document.body; depth += 1) {
    let part = node.tagName.toLowerCase()
    if (node.id) {
      parts.unshift(`#${node.id}`)
      break
    }
    const cls = Array.from(node.classList)
      // Hashed build classes change every deploy; naming them is worse than useless.
      .filter((c) => !/[0-9]{3,}|^css-|^sc-/.test(c))
      .slice(0, 2)
    if (cls.length) part += `.${cls.join('.')}`
    const parent = node.parentElement
    if (parent) {
      const sibs = Array.from(parent.children).filter((c) => c.tagName === node!.tagName)
      if (sibs.length > 1) part += `:nth-of-type(${sibs.indexOf(node) + 1})`
    }
    parts.unshift(part)
    node = node.parentElement
  }
  return parts.join(' > ')
}

/**
 * Walk from the clicked element outward and return the most durable anchor
 * available, plus what to tell the author about it.
 */
export function pickAnchor(target: Element): AnchorCandidate {
  let node: Element | null = target
  for (let depth = 0; node && depth < 6; depth += 1) {
    const tour = node.getAttribute('data-fg-tour')
    if (tour) return describe({ kind: 'tour', value: tour, label: label(node, tour) })

    const segment = node.getAttribute('data-cfo-segment')
    const segmentValue = node.getAttribute('data-cfo-segment-value')
    if (segment && segmentValue) {
      return describe({
        kind: 'segment',
        control: segment,
        value: segmentValue,
        label: label(node, `${segment}: ${segmentValue}`),
      })
    }

    const testid = node.getAttribute('data-testid')
    if (testid) return describe({ kind: 'testid', value: testid, label: label(node, testid) })

    const surface = node.getAttribute('data-surface')
    if (surface) return describe({ kind: 'surface', value: surface, label: label(node, surface) })

    node = node.parentElement
  }

  const anchor: TourAnchor = {
    kind: 'css',
    value: derivedCssPath(target),
    brittle: true,
    label: label(target, 'element'),
  }
  const candidate = describe(anchor)
  candidate.suggestedAttribute = suggestAttributeName(target)
  return candidate
}

function describe(anchor: TourAnchor): AnchorCandidate {
  const grade = TOUR_ANCHOR_GRADE[anchor.kind]
  return {
    anchor,
    grade,
    advice: GRADE_ADVICE[grade],
    matches: countAnchorMatches(anchor),
  }
}

/**
 * Propose a `<page>.<thing>` attribute name from the surrounding DOM, so the
 * author's message to an engineer is a concrete diff rather than a request.
 */
export function suggestAttributeName(el: Element): string {
  const surface = el.closest('[data-surface]')?.getAttribute('data-surface') || ''
  const page = surface.replace(/^cfo_/, '') || cfoPageFromPath()
  const text = (el.getAttribute('aria-label') || el.textContent || 'target')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .split('-')
    .slice(0, 3)
    .join('-')
  return `${page || 'cfo'}-${text || 'target'}`.slice(0, 60)
}

const cfoPageFromPath = (): string => {
  const parts = window.location.pathname.split('/').filter(Boolean)
  const i = parts.indexOf('cfo')
  return i >= 0 ? parts[i + 1] || '' : ''
}

/** The exact JSX an engineer should paste, ready to copy. */
export const jsxSnippetFor = (attribute: string): string => `data-fg-tour="${attribute}"`
